import type { Handoff, Track } from "./HostReviewModal";

type TrackList = readonly { id: Track; label: string }[];

const seatLabel = (n: number | null | undefined) => (n === undefined ? "—" : n === null ? "No limit" : n);

// Any track without a limit makes the total unlimited.
const totalSeats = (h: Handoff, tracks: TrackList, key: "available" | "waitlist"): number | null | undefined => {
  if (!h.seats) return undefined;
  let total = 0;
  for (const { id } of tracks) {
    const n = h.seats[id]?.[key];
    if (n === null || n === undefined) return null;
    total += n;
  }
  return total;
};

// Unfilled primary seats; "—" when there is no limit (or no seat data).
const openSeats = (available: number | null | undefined, accepted: number) =>
  typeof available === "number" ? Math.max(available - accepted, 0) : "—";

interface CohortSummaryProps {
  handoff: Handoff;
  tracks: TrackList;
  caption: string;
  // The track HR Eligibility Review checks; its row is highlighted on the handoff step.
  toHr?: Track;
}

// The proposed cohort against its seat targets. Shown on the HR Review handoff step and, for a
// formal review in progress, on the first step so the two ends of the wizard read the same.
const CohortSummary = ({ handoff, tracks, caption, toHr }: CohortSummaryProps) => {
  const totalAvailable = totalSeats(handoff, tracks, "available");
  return (
    <table className="host-review-handoff-table">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <td />
          <th scope="colgroup" colSpan={3}>
            Cohort seats
          </th>
          <th scope="colgroup" colSpan={2}>
            Waitlist
          </th>
          <td />
        </tr>
        <tr>
          <th scope="col">Track</th>
          <th scope="col">Available</th>
          <th scope="col">Accepted</th>
          <th scope="col">Open</th>
          <th scope="col">Seats</th>
          <th scope="col">Waitlisted</th>
          <th scope="col">Rejected</th>
        </tr>
      </thead>
      <tbody>
        {tracks.map(({ id, label }) => {
          const seats = handoff.seats?.[id];
          const accepted = handoff.tracks[id]?.accepted.length ?? 0;
          return (
            <tr key={id} className={id === toHr ? "to-hr" : undefined}>
              <th scope="row">
                {label}
                {id === toHr && <span className="host-review-hr-tag">To HR</span>}
              </th>
              <td>{seatLabel(seats?.available)}</td>
              <td>{accepted}</td>
              <td>{openSeats(seats?.available, accepted)}</td>
              <td>{seats ? seats.waitlist : "—"}</td>
              <td>{handoff.tracks[id]?.waitlisted.length ?? 0}</td>
              <td>{handoff.tracks[id]?.rejected ?? 0}</td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <th scope="row">Total</th>
          <td>{seatLabel(totalAvailable)}</td>
          <td>{handoff.totals.accepted}</td>
          <td>
            {typeof totalAvailable === "number"
              ? tracks.reduce(
                  (n, { id }) =>
                    n + Number(openSeats(handoff.seats?.[id]?.available, handoff.tracks[id]?.accepted.length ?? 0)),
                  0
                )
              : "—"}
          </td>
          <td>{seatLabel(totalSeats(handoff, tracks, "waitlist"))}</td>
          <td>{handoff.totals.waitlisted}</td>
          <td>{handoff.totals.rejected}</td>
        </tr>
      </tfoot>
    </table>
  );
};

export default CohortSummary;

type TrackState = { order?: number[] | null; decisions?: Record<string, string>; ended?: boolean };

interface ReviewProgressProps {
  tracks: TrackList;
  state: Partial<Record<Track, TrackState>> | undefined;
  // Index into tracks of where the review resumes; tracks.length means the HR Review handoff.
  current: number;
}

type ChipStatus = "done" | "current" | "upcoming";

// A compact stepper: one chip per track plus the handoff, so where the review stands reads at a glance.
export const ReviewProgress = ({ tracks, state, current }: ReviewProgressProps) => {
  const chips: { key: string; label: string; status: ChipStatus; detail: string }[] = tracks.map(({ id, label }, i) => {
    const t = state?.[id];
    const total = t?.order?.length ?? 0;
    const decided = Object.keys(t?.decisions ?? {}).length;
    const status: ChipStatus = t?.ended ? "done" : i === current ? "current" : "upcoming";
    const detail = t?.ended
      ? `${decided} decided`
      : t?.order
        ? `${decided} of ${total} decided`
        : "Not started";
    return { key: id, label, status, detail };
  });
  chips.push({
    key: "handoff",
    label: "HR Review",
    status: current >= tracks.length ? "current" : "upcoming",
    detail: current >= tracks.length ? "Ready to send" : "Waiting",
  });

  return (
    <ol className="host-review-progress-steps" aria-label="Review progress">
      {chips.map((c, i) => (
        <li key={c.key} className={`host-review-progress-step ${c.status}`} aria-current={c.status === "current" ? "step" : undefined}>
          <span className="host-review-progress-icon" aria-hidden="true">
            {c.status === "done" ? "✓" : i + 1}
          </span>
          <span className="host-review-progress-text">
            <strong>{c.label}</strong>
            <small>{c.detail}</small>
          </span>
        </li>
      ))}
    </ol>
  );
};
